import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Alert,
  CircularProgress,
  Stack,
  LinearProgress,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Person,
  CheckCircle,
  ArrowForward,
  ArrowBack,
  Phone,
  AccountCircle,
  ShieldOutlined,
  ScheduleOutlined,
  GroupsOutlined,
  VerifiedOutlined
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { userSettingsService } from '../../services/user/userSettingsService';
import { confirmationEmailService } from '../../services/email/confirmationEmailService';
import {
  PROFESSIONAL_TYPES,
  ProfessionalType,
  ProfessionalPermissions
} from '../../config/professionalTypes';
import { db } from '../../config/firebase';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

/** Depois do primeiro acesso, volta para a página que o usuário abriu (antes ia sempre ao painel). */
function afterOnboardingPath(): string {
  const { pathname, search } = window.location;
  if (!pathname || pathname === '/' || pathname.startsWith('/onboarding') || pathname === '/login') return '/dashboard';
  return `${pathname}${search}`;
}

interface OnboardingData {
  name: string;
  phone: string;
  professionalType: string;
}

function summarizePermissionHighlights(perms: ProfessionalPermissions): string[] {
  const out: string[] = [];
  if (perms.patients_view) out.push('Pacientes');
  if (perms.appointments_view) out.push('Agenda');
  if (perms.ehr_view) out.push('Prontuário eletrônico');
  if (perms.telemedicine_access) out.push('Telemedicina');
  if (perms.inventory_view) out.push('Farmácia e estoque');
  if (perms.financial_view) out.push('Financeiro');
  if (perms.professionals_view) out.push('Equipa');
  if (perms.admin_access) out.push('Administração');
  return out.slice(0, 8);
}

const HERO_POINTS = [
  { icon: ScheduleOutlined, text: 'Agenda, pacientes e prontuário num só lugar' },
  { icon: ShieldOutlined, text: 'Dados tratados com boas práticas de segurança e privacidade' },
  { icon: GroupsOutlined, text: 'Feito para clínicas e profissionais que querem menos papel e mais tempo com pacientes' }
];

const OnboardingPage: React.FC = () => {
  const theme = useTheme();
  const { currentUser, clinicId: authClinicId } = useAuthContext();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<OnboardingData>({
    name: '',
    phone: '',
    professionalType: ''
  });

  useEffect(() => {
    if (currentUser?.displayName && !formData.name) {
      setFormData((prev) => ({ ...prev, name: currentUser.displayName || '' }));
    }
  }, [currentUser?.displayName]);

  const steps = ['O seu perfil', 'Confirmação'];

  const selectedTypeConfig = formData.professionalType
    ? PROFESSIONAL_TYPES[formData.professionalType as ProfessionalType]
    : null;

  const permissionChips = useMemo(() => {
    if (!selectedTypeConfig) return [];
    return summarizePermissionHighlights(selectedTypeConfig.defaultPermissions);
  }, [selectedTypeConfig]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const resolveClinicId = (): string => {
    if (!currentUser) return '';
    return authClinicId || currentUser.clinicId || currentUser.uid;
  };

  const ensureProfessionalDocument = async () => {
    if (!currentUser) return;

    try {
      const profRef = doc(db, 'professionals', currentUser.uid);
      const profSnap = await getDoc(profRef);

      const typeConfig = PROFESSIONAL_TYPES[formData.professionalType as ProfessionalType];
      const specialtyName = typeConfig?.label || formData.professionalType || 'Profissional de Saúde';
      const clinicId = resolveClinicId();

      if (!profSnap.exists()) {
        await setDoc(profRef, {
          id: currentUser.uid,
          uid: currentUser.uid,
          name: formData.name || currentUser.displayName || currentUser.email?.split('@')[0] || '',
          email: currentUser.email || '',
          phone: formData.phone || '',
          cpf: '',
          crm: '',
          specialty: specialtyName,
          specialties: [specialtyName],
          role: formData.professionalType || 'profissional',
          status: 'ativo',
          clinicId,
          appointmentDuration: 30,
          acceptsTelemedicine: false,
          bio: '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        await updateDoc(profRef, {
          name: formData.name || profSnap.data().name || '',
          phone: formData.phone || profSnap.data().phone || '',
          specialty: profSnap.data().specialty || specialtyName,
          clinicId: clinicId || profSnap.data().clinicId,
          updatedAt: serverTimestamp()
        });
      }

      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists() && userSnap.data().role === 'patient') {
        await updateDoc(userRef, { role: 'professional', updatedAt: serverTimestamp() });
      }
    } catch (err) {
      console.warn('⚠️ [ONBOARDING] Erro ao criar documento de profissional (não crítico):', err);
    }
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      console.error('❌ [ONBOARDING] Usuário não autenticado');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      try {
        await userSettingsService.saveProfileSettings(currentUser.uid, {
          name: formData.name,
          email: currentUser.email || '',
          phone: formData.phone,
          professionalType: formData.professionalType,
          specialty: '',
          crm: '',
          uf: '',
          bio: `Usuário do sistema UpClinic`,
          photoURL: ''
        });
      } catch (saveError: any) {
        if (saveError?.code === 'permission-denied' || saveError?.message?.includes('permission')) {
          localStorage.setItem('onboarding_completed_' + currentUser.uid, 'true');
          localStorage.setItem('onboarding_completed', 'true');
          localStorage.setItem('skipOnboarding', 'true');
          localStorage.setItem(
            'user_profile_' + currentUser.uid,
            JSON.stringify({
              name: formData.name,
              email: currentUser.email || '',
              phone: formData.phone,
              professionalType: formData.professionalType
            })
          );
          setLoading(false);
          navigate(afterOnboardingPath());
          return;
        }
        throw saveError;
      }

      await ensureProfessionalDocument();

      localStorage.setItem('onboarding_completed_' + currentUser.uid, 'true');
      localStorage.setItem('onboarding_completed', 'true');

      userSettingsService.invalidateCache(currentUser.uid);
      await new Promise((resolve) => setTimeout(resolve, 500));

      try {
        const settings = await userSettingsService.loadUserSettings(currentUser.uid, false);
        const hasAllRequiredData =
          settings?.profile?.professionalType &&
          settings?.profile?.phone &&
          settings?.profile?.name;
        if (!hasAllRequiredData) {
          console.warn('⚠️ [ONBOARDING] Dados podem não ter sido salvos completamente, mas continuando...');
        }
      } catch {
        console.warn('⚠️ [ONBOARDING] Erro ao verificar dados (não crítico), continuando...');
      }

      try {
        await userSettingsService.markOnboardingComplete(currentUser.uid);
      } catch (markError: any) {
        if (markError?.code === 'permission-denied' || markError?.message?.includes('permission')) {
          console.warn('⚠️ [ONBOARDING] Erro de permissão ao marcar completo, mas já salvo no localStorage');
        } else {
          console.error('❌ [ONBOARDING] Erro ao marcar onboarding:', markError);
        }
        localStorage.setItem('onboarding_completed_' + currentUser.uid, 'true');
        localStorage.setItem('onboarding_completed', 'true');
        localStorage.setItem('skipOnboarding', 'true');
      }

      try {
        let tempPassword = sessionStorage.getItem(`temp_password_${currentUser.uid}`);
        if (!tempPassword && currentUser.email) {
          tempPassword = sessionStorage.getItem(`temp_password_email_${currentUser.email}`);
          if (tempPassword) {
            sessionStorage.setItem(`temp_password_${currentUser.uid}`, tempPassword);
            sessionStorage.removeItem(`temp_password_email_${currentUser.email}`);
          }
        }
        const hasPassword = !!tempPassword;

        await confirmationEmailService.sendConfirmationEmail({
          email: currentUser.email || '',
          name: formData.name,
          loginUrl: `${window.location.origin}/login`,
          hasPassword,
          password: tempPassword || undefined
        });

        if (tempPassword) {
          sessionStorage.removeItem(`temp_password_${currentUser.uid}`);
          if (currentUser.email) {
            sessionStorage.removeItem(`temp_password_email_${currentUser.email}`);
          }
        }
      } catch (emailError) {
        console.error('⚠️ [ONBOARDING] Erro ao enviar email (continuando mesmo assim):', emailError);
      }

      localStorage.removeItem('upclinic_dashboard_tour_completed');
      sessionStorage.setItem('start_tour_after_onboarding', 'true');

      // CompleteRegistration is fired once at account creation in AuthContext
      // (not again here) so Meta ads optimize on real signups without duplicates.

      setLoading(false);
      window.location.href = afterOnboardingPath();
    } catch (error: any) {
      console.error('❌ [ONBOARDING] Erro no onboarding:', error);

      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        localStorage.setItem('onboarding_completed_' + currentUser.uid, 'true');
        localStorage.setItem('skipOnboarding', 'true');
        setLoading(false);
        window.location.href = afterOnboardingPath();
        return;
      }

      setError('Erro ao salvar configurações. Tente novamente.');
      setLoading(false);
    }
  };

  const isStepValid = (step: number): boolean => {
    if (step === 0) {
      return (
        formData.name.trim() !== '' &&
        formData.phone.trim() !== '' &&
        formData.professionalType !== ''
      );
    }
    return true;
  };

  const progress = ((activeStep + 1) / steps.length) * 100;

  const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: 'background.default'
      }}
    >
      {/* Painel esquerdo — narrativa e conversão */}
      <Box
        sx={{
          flex: { xs: 'none', md: '0 0 42%' },
          minHeight: { xs: 'auto', md: '100vh' },
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 8 },
          background: primaryGradient,
          color: 'primary.contrastText',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.12,
            backgroundImage: `radial-gradient(circle at 20% 30%, ${alpha('#fff', 0.9)} 0%, transparent 45%),
              radial-gradient(circle at 80% 70%, ${alpha('#fff', 0.5)} 0%, transparent 40%)`,
            pointerEvents: 'none'
          }}
        />
        <Stack spacing={3} sx={{ position: 'relative', maxWidth: 420 }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL || ''}/upclinic-logo-transparent.png`}
            alt="UpClinic"
            sx={{
              height: { xs: 52, md: 64 },
              width: 'auto',
              maxWidth: 340,
              objectFit: 'contain',
              objectPosition: 'left center',
              display: 'block',
              alignSelf: 'flex-start'
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem' },
              lineHeight: 1.15,
              letterSpacing: '-0.03em'
            }}
          >
            O seu consultório digital, pronto em poucos minutos
          </Typography>
          <Typography sx={{ opacity: 0.92, fontSize: '1.05rem', lineHeight: 1.6 }}>
            Indique o seu perfil e nós pré-configuramos o que faz sentido para o seu dia a dia — sem
            folhas de cálculo nem sistemas desconjuntados.
          </Typography>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {HERO_POINTS.map(({ icon: Icon, text }) => (
              <Stack key={text} direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    mt: 0.25,
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha('#fff', 0.15)
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ opacity: 0.95, fontSize: '0.95rem', lineHeight: 1.5 }}>{text}</Typography>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 2, opacity: 0.9 }}>
            <VerifiedOutlined sx={{ fontSize: 22 }} />
            <Typography variant="body2">Leva menos de 2 minutos · Sem cartão neste passo</Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Formulário */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 3, md: 6 },
          px: { xs: 2, sm: 3 }
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          style={{ width: '100%', maxWidth: 560 }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: `0 24px 80px ${alpha(theme.palette.common.black, 0.06)}`,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
                  Primeiro acesso
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Passo {activeStep + 1} de {steps.length}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  mt: 2,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  '& .MuiLinearProgress-bar': { borderRadius: 2 }
                }}
              />
            </Box>

            {error && (
              <Box sx={{ px: 3, pt: 2 }}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              </Box>
            )}

            <AnimatePresence mode="wait">
              {activeStep === 0 && (
                <motion.div
                  key="step0"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  <Box sx={{ p: 3, pt: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: '-0.02em' }}>
                      Quem é o profissional?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Estes dados aparecem em marcações e comunicações com pacientes.
                    </Typography>

                    <Stack spacing={2.5}>
                      <TextField
                        fullWidth
                        label="Nome completo"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                        placeholder="Ex.: Dra. Maria Santos"
                        InputProps={{
                          startAdornment: <AccountCircle sx={{ mr: 1, color: 'action.active' }} />
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        fullWidth
                        label="Telefone / WhatsApp"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required
                        placeholder="(11) 99999-9999"
                        InputProps={{
                          startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />

                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Person fontSize="small" color="primary" />
                          Tipo de profissional
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Ao escolher, o sistema alinha módulos e permissões iniciais ao seu perfil.
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                            gap: 1.25,
                            maxHeight: { xs: 280, sm: 'none' },
                            overflowY: { xs: 'auto', sm: 'visible' },
                            pr: { xs: 0.5, sm: 0 },
                            pb: 0.5
                          }}
                        >
                          {Object.entries(PROFESSIONAL_TYPES).map(([key, config]) => {
                            const selected = formData.professionalType === key;
                            const Icon = config.icon;
                            return (
                              <Box
                                key={key}
                                component="button"
                                type="button"
                                onClick={() => handleInputChange('professionalType', key)}
                                sx={{
                                  m: 0,
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: '2px solid',
                                  borderColor: selected ? 'primary.main' : 'divider',
                                  bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                                  transition: 'border-color 0.2s, background-color 0.2s, transform 0.15s',
                                  '&:hover': {
                                    borderColor: selected ? 'primary.main' : alpha(theme.palette.primary.main, 0.35),
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                              >
                                <Stack spacing={0.75}>
                                  <Icon
                                    sx={{
                                      fontSize: 26,
                                      color: selected ? 'primary.main' : 'text.secondary'
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                                    {config.label}
                                  </Typography>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      {selectedTypeConfig && permissionChips.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          transition={{ duration: 0.3 }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.info.main, 0.06),
                              border: '1px solid',
                              borderColor: alpha(theme.palette.info.main, 0.2)
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Já fica preparado com acesso a:
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                              {permissionChips.map((label) => (
                                <Chip
                                  key={label}
                                  label={label}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    border: 'none'
                                  }}
                                />
                              ))}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                              Registro profissional típico: {selectedTypeConfig.registrationLabel} ·{' '}
                              {selectedTypeConfig.board}
                            </Typography>
                          </Box>
                        </motion.div>
                      )}
                    </Stack>
                  </Box>
                </motion.div>
              )}

              {activeStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  <Box sx={{ p: 3, pt: 2, textAlign: 'center' }}>
                    <motion.div
                      initial={{ scale: 0.85 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                    >
                      <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
                    </motion.div>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      Tudo certo, {formData.name.trim().split(/\s+/)[0] || 'bem-vindo'}!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                      Vamos abrir o painel com o seu perfil e permissões alinhadas. Você pode completar registro
                      profissional, endereço da clínica e mais detalhes depois nas configurações.
                    </Typography>
                    <Stack
                      spacing={1.5}
                      sx={{
                        textAlign: 'left',
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'action.hover',
                        maxWidth: 400,
                        mx: 'auto'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Resumo
                      </Typography>
                      <Typography variant="body2">
                        <strong>Nome:</strong> {formData.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Telefone:</strong> {formData.phone}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Perfil:</strong>{' '}
                        {selectedTypeConfig?.label || formData.professionalType || '—'}
                      </Typography>
                      {permissionChips.length > 0 && (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ pt: 0.5 }}>
                          {permissionChips.map((label) => (
                            <Chip key={label} label={label} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                gap: 2
              }}
            >
              <Button
                onClick={handleBack}
                disabled={activeStep === 0 || loading}
                startIcon={<ArrowBack />}
                variant="text"
                color="inherit"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Voltar
              </Button>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    py: 1.25,
                    borderRadius: 2,
                    boxShadow: 'none',
                    background: primaryGradient,
                    '&:hover': { boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}` }
                  }}
                >
                  {loading ? 'Finalizando…' : 'Entrar no painel'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isStepValid(activeStep)}
                  endIcon={<ArrowForward />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    py: 1.25,
                    borderRadius: 2,
                    boxShadow: 'none',
                    background: primaryGradient,
                    '&:hover': { boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}` }
                  }}
                >
                  Continuar
                </Button>
              )}
            </Stack>
          </Card>
        </motion.div>
      </Box>
    </Box>
  );
};

export default OnboardingPage;
